import React from 'react';
import { ImagesIcon } from '@phosphor-icons/react';
import Image from '@plone/volto/components/theme/Image/Image';
import { withBlockExtensions } from '@plone/volto/helpers';
import {
  MPFBlockCardHeader,
  MPFButton,
  MPFCardCarousel,
  MPFLink,
} from 'volto-ministerio-publico-federal/components/UI';
import { resolveBrowserHref } from 'volto-ministerio-publico-federal/components/View/CapaView/helpers';
import { EditItemButton } from 'volto-ministerio-publico-federal/components/View/CapaView/EditItemButton';

import {
  getSubHomeCarrosselImageProps,
  SUBHOME_CARROSSEL_MAX_ITEMS,
  SUBHOME_CARROSSEL_SLIDES,
  SUBHOME_CARROSSEL_TITLE,
  subHomeCarrosselHasContent,
  toSubHomeCarrosselCards,
  type SubHomeCarrosselCard,
  type SubHomeCarrosselData,
} from './helpers';

interface ViewProps {
  data: SubHomeCarrosselData;
  block: string;
  isEditMode?: boolean;
  /** Presente só no editor: abre o card de índice N na barra lateral. */
  onEditItem?: (index: number) => void;
}

const IMAGE_CLASS =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

/* `not-prose` desliga a tipografia de texto corrido que a página herda do
   editor (`[class*='__editor-content']`, em `base/_editor-content.scss`):
   as faixas trazem a própria tipografia, e sem isto o `prose` recolore e
   redimensiona títulos, links e listas de dentro do cartão. Mesmo recurso
   que `mpfNoticiasPorTema` e `mpfCampanhas` já usam. */
const ROOT_CLASS = 'mpf-subhome-card not-prose';

const Notice: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="mpf-subhome-card__placeholder">{children}</p>
);

/**
 * Faixa "Carrossel destaques" da sub-home (bloco `mpfSubHomeCarrossel`).
 *
 * Mesmo desenho da faixa "Onde o MPF atua?" da capa — card de texto à
 * esquerda, carrossel de cards à direita — mas SEM as abas: aqui há um único
 * conjunto de conteúdo, então os campos são achatados em vez dos `tema{N}*`.
 *
 * Sem conteúdo a faixa não é renderizada no site; no editor ela aparece com
 * um aviso, para quem edita enxergar o bloco que acabou de inserir.
 */
export const MpfSubHomeCarrosselBlockView: React.FC<ViewProps> = ({
  data,
  block,
  isEditMode,
  onEditItem,
}) => {
  const titleId = `subhome-carrossel-${block}`;
  const cards = React.useMemo(
    () => toSubHomeCarrosselCards(data?.items),
    [data?.items],
  );
  const overflowCount =
    (data?.items?.length ?? 0) - SUBHOME_CARROSSEL_MAX_ITEMS;

  if (!subHomeCarrosselHasContent(data)) {
    return isEditMode ? (
      <section className={ROOT_CLASS}>
        <MPFBlockCardHeader
          icon={
            <ImagesIcon
              aria-hidden="true"
              className="h-6 w-6 shrink-0 text-[color:var(--orange-600)]"
            />
          }
          title={data?.title || SUBHOME_CARROSSEL_TITLE}
          titleId={titleId}
          ruleClassName="bg-[color:var(--orange-600)]"
        />
        <Notice>
          Escreva o texto de apresentação e escolha os cards na barra lateral.
        </Notice>
      </section>
    ) : null;
  }

  return (
    <section aria-labelledby={titleId} className={ROOT_CLASS}>
      <MPFBlockCardHeader
        icon={
          <ImagesIcon
            aria-hidden="true"
            className="h-6 w-6 shrink-0 text-[color:var(--orange-600)]"
          />
        }
        title={data?.title || SUBHOME_CARROSSEL_TITLE}
        titleId={titleId}
        ruleClassName="bg-[color:var(--orange-600)]"
      />

      {/* `grid-cols-1` explícito e `min-w-0` nos filhos: sem trilha declarada,
          a coluna implícita do mobile é `max-content` e o carrossel estoura a
          largura — mesma armadilha documentada em `View/CapaView/index.tsx`. */}
      {/* 5/7, e não os 4/8 da faixa "Onde o MPF atua?" da capa: no Figma o
          card de texto tem 389px contra 399px do carrossel dentro dos 820 do
          cartão — praticamente meio a meio. Com 4/12 o card ficava estreito
          demais e as fotos, pequenas demais em relação ao design. */}
      <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="order-1 min-w-0 lg:col-span-5">
          <TextoCard data={data} />
        </div>

        <div className="order-2 min-w-0 lg:col-span-7">
          <MPFCardCarousel
            name={`subhome-carrossel-${block}`}
            arrowTopClassName="top-[35%]"
            breakpoints={{
              640: { slidesPerView: 2, spaceBetween: 24 },
              1024: {
                slidesPerView: SUBHOME_CARROSSEL_SLIDES,
                spaceBetween: 24,
              },
            }}
            labels={{
              prev: 'Cards anteriores',
              next: 'Próximos cards',
              first: 'Este é o primeiro card',
              last: 'Este é o último card',
              bullet: 'Ir para o card {{index}}',
            }}
            slides={cards.map((card) => ({
              key: card.key,
              content: (
                <CarrosselCard
                  card={card}
                  isEditMode={isEditMode}
                  onEditItem={onEditItem}
                />
              ),
            }))}
          />
        </div>
      </div>

      {isEditMode && overflowCount > 0 && (
        <Notice>
          {overflowCount === 1
            ? 'O último card não aparece: a faixa exibe no máximo 10.'
            : `Os últimos ${overflowCount} cards não aparecem: a faixa exibe no máximo 10.`}
        </Notice>
      )}
    </section>
  );
};

/**
 * Card claro à esquerda: chapéu, texto e o botão da página.
 *
 * Sem traço próprio: quem fecha a faixa é a moldura do `.mpf-subhome-card`. O
 * card se separa do fundo pelo degradê de teal, que mora no SCSS
 * (`.mpf-card-teal`, em `_capa-onde-atua.scss`) porque precisa de duas camadas
 * de fundo — daí também a ausência de `bg-*`: as utilities do Tailwind entram
 * depois do nosso CSS e sobrescreveriam o degradê.
 */
const TextoCard: React.FC<{ data: SubHomeCarrosselData }> = ({ data }) => {
  const ctaHref = resolveBrowserHref(data?.ctaHref);

  return (
    <div className="mpf-card-teal flex h-full flex-col rounded-2xl p-4 shadow-center dark:bg-night-800">
      {data?.subtitulo && (
        <p className="text-base font-normal text-gray-500 dark:text-night-400">
          {data.subtitulo}
        </p>
      )}
      {data?.texto && (
        <p className="mt-2 text-lg font-normal text-on-surface dark:text-night-100">
          {data.texto}
        </p>
      )}

      {data?.ctaLabel && ctaHref && (
        /* `mt-auto` cola o botão no rodapé do card, que estica junto com a
           coluna do carrossel; o `pt-6` é o respiro mínimo quando o texto é
           longo o bastante para não sobrar espaço. */
        <div className="mt-auto pt-6">
          <MPFButton
            as={MPFLink}
            href={ctaHref}
            fullWidth
            openLinkInNewTab={data.ctaExternal || undefined}
          >
            {data.ctaLabel}
          </MPFButton>
        </div>
      )}
    </div>
  );
};

/** Card do carrossel: foto arredondada em cima, legenda abaixo. */
const CarrosselCard: React.FC<{
  card: SubHomeCarrosselCard;
  isEditMode?: boolean;
  onEditItem?: (index: number) => void;
}> = ({ card, isEditMode, onEditItem }) => {
  const imageProps = getSubHomeCarrosselImageProps(card);

  return (
    <article className="group relative min-w-0">
      {onEditItem && (
        <EditItemButton
          label={`Editar card: ${card.title || 'sem título'}`}
          onClick={() => onEditItem(card.sourceIndex)}
          className="absolute right-2 top-2 z-10"
        />
      )}

      {/* `relative` próprio: a foto se posiciona pela moldura da imagem, não
          pelo card inteiro (que inclui a legenda). */}
      <div className="relative aspect-[3/2] overflow-hidden rounded-2xl bg-gray-200 dark:bg-night-700">
        {imageProps && (
          <Image
            {...imageProps}
            alt={card.alt}
            loading="lazy"
            className={IMAGE_CLASS}
          />
        )}
      </div>

      <p className="mt-2 line-clamp-2 text-base font-medium text-on-surface dark:text-night-100">
        <CardLabel card={card} isEditMode={isEditMode} />
      </p>
    </article>
  );
};

/**
 * Legenda do card. Vira link quando há conteúdo escolhido — aí o `after`
 * estende a área clicável para o card inteiro sem inchar o nome acessível.
 *
 * Um `<a>` sem `href` não é clicável nem focável e ainda assim é anunciado
 * como link. No editor, navegar seria pior ainda: tira o editor da página.
 */
const CardLabel: React.FC<{
  card: SubHomeCarrosselCard;
  isEditMode?: boolean;
}> = ({ card, isEditMode }) => {
  if (!card.href || isEditMode) return <>{card.title}</>;

  return (
    <MPFLink
      href={card.href}
      openInNewTab={card.external || undefined}
      className="after:absolute after:inset-0 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    >
      {card.title}
    </MPFLink>
  );
};

export default withBlockExtensions(MpfSubHomeCarrosselBlockView);
